/**
 * Socket configuration utilities
 */
import { env } from '@/env.mjs';

/**
 * Get the socket URL based on the current environment
 * @returns The socket URL to use for connections
 */
export function getSocketUrl(): string {
  // NEXT_PUBLIC_SOCKET_URL is baked into the client bundle at BUILD time, and
  // production runs the exact bytes staging built (deploy-production.sh rsyncs
  // them), so on prod the baked value is staging's origin. In the browser we
  // therefore only honour the baked URL when its hostname matches the page we
  // are actually on (dev: localhost:43000 page + localhost:43001 socket both
  // match on hostname); any cross-host bake falls back to same-origin, which
  // is where the Nginx proxy serves the socket on staging and prod.
  const envUrl = process.env.NEXT_PUBLIC_SOCKET_URL;

  if (typeof window !== 'undefined') {
    if (envUrl) {
      try {
        if (new URL(envUrl).hostname === window.location.hostname) {
          return envUrl;
        }
      } catch {
        // Malformed env value: ignore it and use same-origin.
      }
    }
    return `${window.location.protocol}//${window.location.host}`;
  }

  if (envUrl) {
    return envUrl;
  }

  // Server-side fallback (should rarely be used for client connections)
  const fallbackUrl = process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3000';
  // console.log('Using fallback socket URL:', fallbackUrl);
  return fallbackUrl;
}

/**
 * Get the socket path
 * @returns The socket path to use for connections
 */
export function getSocketPath(): string {
  // Use NEXT_PUBLIC_SOCKET_PATH for client-side access
  if (typeof window !== 'undefined') {
    // In production, we need to use the path that Nginx expects
    const isProduction = process.env.NODE_ENV === 'production';
    const path = isProduction
      ? (process.env.NEXT_PUBLIC_SOCKET_PATH || '/ws')
      : (process.env.NEXT_PUBLIC_SOCKET_PATH || '/api/socket');

    // console.log('Using client-side socket path:', path);
    return path;
  }

  // Use env.SOCKET_PATH on the server side
  const serverPath = env.SOCKET_PATH || '/api/socket';
  // console.log('Using server-side socket path:', serverPath);
  return serverPath;
}

/**
 * Get socket connection options
 * @returns Common socket connection options
 */
export function getSocketOptions(skipAuth: boolean = false) {
  // In production, we need different settings for the Nginx proxy
  const isProduction = process.env.NODE_ENV === 'production';

  // NOTE: the handshake is authenticated server-side from the NextAuth
  // session cookie, which the browser sends automatically with the polling /
  // websocket upgrade because `withCredentials: true` is set below. We do NOT
  // read the cookie in JS — `next-auth.session-token` is HttpOnly, so
  // `document.cookie` can never see it. The socket server decodes the cookie
  // (see scripts/build-server.js → io.use middleware) and derives the user +
  // role authoritatively. The `auth` payload below carries only advisory
  // identity for logging/notification subscription; the server never trusts it
  // for room membership.
  return {
    // Don't include path here as it's set separately in the io() call.
    //
    // Polling-only in production: staging.choice.graphics and app.choice.graphics
    // are fronted by Cloudflare, which rejects WebSocket upgrade with HTTP 400
    // "Invalid Upgrade header" at the edge — even with the zone-level WebSockets
    // toggle on. Polling works fine through CF, so we skip the WS transport
    // entirely on prod/staging to avoid the noisy retry loop in the console.
    // Re-enable once the CF-side issue is diagnosed (likely needs a Spectrum
    // endpoint or a non-proxied subdomain with its own TLS cert).
    transports: isProduction ? ['polling'] : ['polling', 'websocket'],
    reconnection: true,
    reconnectionAttempts: isProduction ? 5 : 10,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    timeout: 60000,
    autoConnect: true,
    withCredentials: true,
    forceNew: true,
    auth: {
      skipAuth
    }
  };
}

/**
 * Initialize a socket connection with the correct configuration
 * @param skipAuth Whether to skip authentication
 * @returns Socket.IO client configuration object
 */
export function getSocketConfig(skipAuth: boolean = false) {
  const url = getSocketUrl();
  const path = getSocketPath();
  const options = getSocketOptions(skipAuth);

  // console.log('Socket configuration:', {
  //   url,
  //   path,
  //   nodeEnv: process.env.NODE_ENV,
  //   isProduction: process.env.NODE_ENV === 'production'
  // });

  return {
    url,
    path,
    options
  };
}
